<template>
    <div id="shuttle-bus">
        <ViewContainer :title="`Shuttle Bus`" :numberOfItems="shuttleBusTable.numberOfItems ? shuttleBusTable.numberOfItems : 0" :isLoading="isLoading">
            <template v-slot:header>
                <ContainerHeader
                    :searchInput="searchInput"
                    :searchString="searchInput.value"
                    @update:searchString="searchInput.value = $event"
                    :dropdownMenu="containerDropdownMenu"
                    :buttons="containerButtons"
                />
            </template>
            <template v-slot:content>
                <Table :table="shuttleBusTable" />
            </template>
        </ViewContainer>
    </div>
    <UploadDialog v-if="uploadDialog.isVisible" :dialog="uploadDialog" :hasEndDate="false" @update:isVisible="uploadDialog.isVisible = $event" />
</template>

<script>
import { fetchData as fetchSchedules, postData as addSchedule, deleteData as deleteSchedules } from "@/modules/shuttle-bus.js";
import ViewContainer from "@/components/ViewContainer";
import ContainerHeader from "@/components/ContainerHeader";
import Table from "@/components/Table";
import UploadDialog from "@/components/UploadDialog";
import moment from "moment";

export default {
    name: "ShuttleBus",
    components: {
        ViewContainer,
        ContainerHeader,
        Table,
        UploadDialog,
    },
    data() {
        return {
            isLoading: false,
            searchInput: {
                placeholder: "Search by title, author, remarks",
                value: "",
                callback: () => this.searchSchedules(),
            },
            containerDropdownMenu: {
                name: "Action",
                items: [
                    {
                        name: "Delete",
                        icon: "el-icon-delete",
                        value: 0,
                        style: "color: #de350b;",
                    },
                ],
                callback: command => this.handleAction(command),
            },
            containerButtons: [
                {
                    name: "Upload",
                    icon: "el-icon-upload",
                    callback: () => this.handleUploadDialog(),
                },
            ],
            shuttleBusTable: {
                isLoading: false,
                handleRowClick: (row, column, e) => this.getPdf(row, column, e),
                cols: [
                    {
                        label: "title",
                        sortable: true,
                        prop: "title",
                        minWidth: "176",
                        className: "link-type",
                    },
                    {
                        label: "author",
                        sortable: false,
                        prop: "author",
                        minWidth: "95",
                    },
                    {
                        label: "publish date",
                        sortable: true,
                        prop: "published_date",
                        minWidth: "152",
                    },
                    {
                        label: "remarks",
                        sortable: false,
                        prop: "remarks",
                        minWidth: "120",
                    },
                ],
                data: [],
                numberOfItems: null,
                multipleSelection: [],
                handleSelectionChange: rows => this.handleSelectionChange(rows),
                handleDelete: (index, row) => this.handleDelete(index, row),
                pagination: {
                    currentPage: 1,
                    pageSize: 10,
                    handleSizeChange: pageSize => this.handleSizeChange(pageSize),
                    handleCurrentChange: currentPage => this.handleCurrentChange(currentPage),
                },
            },
            uploadDialog: {
                isVisible: false,
                title: "Shuttle Bus",
                upload: form => this.addSchedule(form),
            },
        };
    },
    mounted() {
        const vm = this;
        vm.fetchSchedules();
    },
    methods: {
        async fetchSchedules() {
            this.isLoading = true;
            let pageSize = this.shuttleBusTable.pagination.pageSize;
            let pageIndex = this.shuttleBusTable.pagination.currentPage;
            let searchString = this.searchInput.value;
            try {
                const result = await fetchSchedules(pageSize, pageIndex, searchString);
                if (result) {
                    this.shuttleBusTable.data = result.data;
                    this.shuttleBusTable.numberOfItems = result.count;
                }
            } finally {
                this.isLoading = false;
            }
        },
        async addSchedule(form) {
            const schedule = {
                title: form.title,
                file: form.file.raw,
                publishDate: moment(form.publishDate).format("YYYY-MM-DD HH:mm"),
                endDate: moment(form.endDate).format("YYYY-MM-DD HH:mm"),
                remarks: form.remarks,
            };
            const result = await addSchedule(schedule);
            if (result) {
                this.uploadDialog.isVisible = false;
                let name = form.file.name;
                this.$notify({
                    title: "Shuttle bus schedule uploaded successfully",
                    message: name ? name + " was uploaded successfully" : "",
                    type: "success",
                });
                this.fetchSchedules();
            }
        },
        async deleteSchedules(action, scheduleList) {
            try {
                const confirmMsg = await this.$confirm("This will permanently delete the shuttle bus schedule(s). Continue?", "Warning", {
                    confirmButtonText: "Confirm",
                    cancelButtonText: "Cancel",
                    cancelButtonClass: "cancel-btn",
                    type: "warning",
                });
                if (confirmMsg === "confirm") {
                    this.isLoading = true;
                    const result = await deleteSchedules(action, scheduleList);
                    if (result) {
                        this.$notify({
                            title: "Shuttle bus schedule(s) deleted successfully",
                            type: "success",
                        });
                    }
                    this.fetchSchedules();
                }
            } catch (error) {
                console.log(error);
            } finally {
                this.isLoading = false;
            }
        },
        // header
        handleUploadDialog() {
            this.uploadDialog.isVisible = true;
        },
        handleAction() {
            if (this.shuttleBusTable.multipleSelection.length > 0) {
                this.deleteSchedules(this.shuttleBusTable.multipleSelection);
            }
        },
        // table
        searchSchedules() {
            this.shuttleBusTable.pagination.currentPage = 1;
            this.fetchSchedules();
        },
        handleDelete(index, row) {
            this.deleteSchedules([row.id]);
        },
        handleSelectionChange(rows) {
            this.shuttleBusTable.multipleSelection = rows.map(post => post.id);
        },
        getPdf(row, column) {
            if (column.property == "title" && row.file_path) {
                window.open(row.file_path, "_blank");
            } else {
                return;
            }
        },
        // pagination
        handleSizeChange(pageSize) {
            this.shuttleBusTable.pagination.pageSize = pageSize;
            this.fetchSchedules();
        },
        handleCurrentChange(currentPage) {
            this.shuttleBusTable.pagination.currentPage = currentPage;
            this.fetchSchedules();
        },
    },
};
</script>

<style lang="scss">
#shuttle-bus {
    height: 100%;
}
</style>
